import type { CgOrder, CgOrderItem } from './types';

/**
 * The Production PO, as a shape with no money in it.
 *
 * This file is the whole zero-pricing guarantee. `buildProductionPo` is the
 * only place an order and its items are read, and what it returns carries no
 * price, no cost, no margin and no total — so the document component cannot
 * render a figure it was never handed. Hiding money with CSS would not do:
 * the existing print trick uses `visibility: hidden`, which leaves the value
 * in the DOM for anyone who opens the page source or saves the file.
 *
 * The PO goes to the factory. What the client pays is not the factory's
 * business, and one careless prop spread is all it takes for it to become
 * their business permanently.
 */

/** Our own letterhead. Fixed text, not data — it is who is sending the PO. */
export const PO_SENDER = {
  name: '4ocean PBC — Corporate Gifting',
  addressLines: ['1550 NW 1st Ave', 'Boca Raton, FL 33432'],
  email: 'alexjschulze@4ocean.com',
} as const;

export interface ProductionPoLine {
  /** How many to make. */
  units: number;
  /** What it is called on the line. */
  name: string;
  /** Blank when the product has no SKU — never printed as empty brackets. */
  sku: string;
  /** Blank when there is nothing to specify. */
  spec: string;
}

export interface ProductionPoLink {
  url: string;
  /** A label, or '' — the renderer falls back to the URL, never to "link". */
  name: string;
}

export interface ProductionPo {
  poNumber: string;
  issuedOn: string;
  orderNo: string;
  manufacturer: string;
  /** Who the sheet is addressed to. Falls back to 'Production Lead'. */
  manufacturerAttn: string;
  /** The factory address, split for printing. Empty when nobody has entered one. */
  manufacturerAddressLines: string[];
  /** One entry per line of the address block, already split. */
  shipToLines: string[];
  /** Null when no date has been committed. Never silently derived. */
  targetShipDate: string | null;
  lines: ProductionPoLine[];
  notes: string;
  designLinks: ProductionPoLink[];
}

/**
 * The addressee, or the fallback.
 *
 * 'Production Lead' was hard-coded in the document component, so every PO to
 * every factory was addressed to the same imaginary person. The name belongs
 * to the supplier record now; the fallback stays so a factory nobody has named
 * still prints an addressee rather than a blank line.
 */
function attnOr(name: string): string {
  return name === '' ? 'Production Lead' : name;
}

/**
 * A PO number that reads as one: the order number already carries its own
 * '#', so 'CG-PO-' plus '#1040' gives the sample's 'CG-PO-#1040' without a
 * second separator sneaking in.
 */
export function poNumberFor(orderNo: string): string {
  return `CG-PO-${orderNo}`;
}

/**
 * Splits the free-text ship-to block into printable lines.
 *
 * The sample printed "CathleenMontano" and
 * "4ocean Philippines,Mintal, Davao City, Philippines,8000" because separate
 * fields were concatenated with a separator that was missing in one place and
 * doubled in another. Here the block is typed once and only ever split, never
 * joined — blank lines are dropped so a stray return does not open a hole in
 * the address.
 */
export function shipToLines(shipTo: string): string[] {
  return shipTo
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/**
 * One printable line per item.
 *
 * The sample ran two items together — "200 × Custom Hidden Sea Bracelets ()1 ×
 * Custom Hidden Sea Bracelets Shipping Fee ()" — under a caption promising one
 * per line. Returning an array rather than a joined string is what makes that
 * impossible to reproduce: the renderer has no string to forget to break.
 *
 * The name falls back to the product type, because `description` is optional
 * on the item and a blank name reads as a missing product rather than a
 * missing description.
 */
export function poLineOf(item: CgOrderItem): ProductionPoLine {
  const described = item.description.trim();
  return {
    units: item.units,
    name: described === '' ? item.product_type : described,
    sku: (item.sku ?? '').trim(),
    spec: (item.spec ?? '').trim(),
  };
}

/**
 * Builds the PO from an order and its items.
 *
 * Takes the full rows and returns the narrow shape: this is the boundary the
 * money stops at. Shipping and handling are NOT lines — they are amounts the
 * client is billed, and the sample's "1 × Custom Hidden Sea Bracelets Shipping
 * Fee" told a factory to manufacture one shipping fee.
 */
export function buildProductionPo(
  order: CgOrder,
  items: CgOrderItem[],
  context: {
    manufacturer: string;
    issuedOn: string;
    designLinks?: ProductionPoLink[];
    attn?: string;
    address?: string;
  },
): ProductionPo {
  return {
    poNumber: poNumberFor(order.order_no),
    issuedOn: context.issuedOn,
    orderNo: order.order_no,
    manufacturer: context.manufacturer,
    manufacturerAttn: attnOr((context.attn ?? '').trim()),
    manufacturerAddressLines: shipToLines(context.address ?? ''),
    shipToLines: shipToLines(order.ship_to ?? ''),
    targetShipDate: order.target_ship_date,
    lines: items.map(poLineOf),
    // notes_to_manufacturer, NOT notes. `notes` is the tracker's internal box
    // — "client is slow to approve", "margin is thin here" — and it used to be
    // printed under NOTES TO MANUFACTURER, which made every internal aside a
    // supplier disclosure by default. The two are separate fields now.
    notes: order.notes_to_manufacturer.trim(),
    designLinks: (context.designLinks ?? []).filter((link) => link.url.trim() !== ''),
  };
}

// ── The frozen PO ──────────────────────────────────────────────────────────

/**
 * A PO exactly as it was issued.
 *
 * These types live here, beside the shape they are converted into, so that the
 * snapshot and the printed document are read together. They describe what
 * issue_po_revision() writes into po_documents.snapshot — every key optional,
 * because a row written by an earlier version of that function is still a
 * valid row and the browser must render it rather than crash on it.
 */
export interface PoSnapshot {
  order_no?: string;
  revision_label?: string;
  client?: string;
  supplier?: string;
  ship_to?: string;
  target_ship_date?: string | null;
  date_po_sent?: string | null;
  issued_at?: string;
  items?: { units: number; name: string; sku: string; spec: string }[];
  proofs?: { component: string; version: number; url: string }[];
  /** Files the factory needs that the client never approves — the logo. */
  reference_files?: { kind: string; label?: string; version: number; url: string }[];
  certificate?: { certificate_no: string; signed_at: string } | null;
  /** Who the sheet was addressed to and where, frozen with the rest of it. */
  manufacturer?: { name?: string; attn?: string; address?: string };
  /** Build instructions. Absent on revisions issued before this field existed. */
  notes_to_manufacturer?: string;
  design_links?: { url: string; name: string }[];
  /**
   * Present on revisions frozen before the row was removed from the sheet, and
   * read by nothing. Kept on the type so an old snapshot still parses.
   *
   * It said whether the CLIENT had paid THEIR invoice, and printed to the
   * factory as "PAYMENT STATUS: PAID" — which reads, to the party being paid,
   * as confirmation that THEY have been. Kyu: "it gives the impression that we
   * already paid the supplier where in fact that's a shopify payment status
   * from/to our client. No use for the Supplier."
   */
  payment_status?: string;
}

export interface PoDocument {
  revision: number;
  label: string;
  issued_at: string;
  superseded_at: string | null;
  reason: string;
  snapshot: PoSnapshot;
}

/**
 * Turns a frozen snapshot into the same shape the printed PO is built from.
 *
 * The point of this function is that there is now ONE PO layout. The sheet TAP
 * prints and the sheet the factory downloads were two different documents with
 * two different field sets, and the one that mattered — the frozen record, the
 * one that watermarks when the ship date moves — was the thinner of the two.
 *
 * Everything is read from the snapshot and nothing from the live order. A
 * value missing from an older snapshot renders as missing; it is never
 * back-filled from today's order, because a PO that quietly gains a field it
 * was not issued with is exactly the drift po_documents exists to stop.
 */
export function poFromSnapshot(doc: PoDocument): ProductionPo {
  const snap = doc.snapshot;
  const orderNo = snap.order_no ?? '';
  return {
    // The revision belongs in the number. Two sheets on a workbench reading
    // "CG-PO-#1039" with different quantities is the failure; "Rev A" and
    // "Rev B" is the fix, and it has to be on the line people quote.
    poNumber: poNumberFor(orderNo) + (snap.revision_label ?? ''),
    issuedOn: doc.issued_at.slice(0, 10),
    orderNo,
    manufacturer: snap.manufacturer?.name ?? snap.supplier ?? '',
    manufacturerAttn: attnOr((snap.manufacturer?.attn ?? '').trim()),
    manufacturerAddressLines: shipToLines(snap.manufacturer?.address ?? ''),
    shipToLines: shipToLines(snap.ship_to ?? ''),
    targetShipDate: snap.target_ship_date ?? null,
    lines: (snap.items ?? []).map((line) => ({
      units: line.units,
      name: line.name,
      sku: (line.sku ?? '').trim(),
      spec: (line.spec ?? '').trim(),
    })),
    notes: (snap.notes_to_manufacturer ?? '').trim(),
    designLinks: (snap.design_links ?? []).filter((link) => link.url.trim() !== ''),
  };
}
